import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../../components/chatBot/ChatNavbar";
import ChatSidebar from "../../components/chatBot/ChatSidebar";
import ChatWindow from "../../components/chatBot/ChatWindow";
import ChatInput from "../../components/chatBot/ChatInput";
import { Menu } from "lucide-react";
import { useTranslation } from "react-i18next";

import { chatbot, teacherchatbot } from "../../services/operations/chatbotAPI";
import {
  createConversation,
  deleteConversation,
  getUserConversations,
  updateConversationTitle,
} from "../../services/operations/conversationAPI";
import { addMessage } from "../../services/operations/messageAPI";
import { fetchCurriculumByClass } from "../../services/operations/curriculumAPI";

export default function ChatBot() {
  const { t } = useTranslation();
  const { conversationId: routeConversationId } = useParams();
  const [chats, setChats] = useState([
    {
      title: "Welcome Chat",
      messages: [
        { role: "assistant", content: "Hi there! How can I help you today?" },
      ],
    },
  ]);
  const [activeChatIndex, setActiveChatIndex] = useState(0);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  const user = useSelector((state) => state.profile?.user);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const sidebarRef = useRef();

  //-----------------------------------select sub and chapter-----------------------------
  // Subject and Chapter state
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedChapter, setSelectedChapter] = useState("");

  // Curriculum data from database
  const [curriculumData, setCurriculumData] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);

  // Fetch curriculum data based on user's class
  useEffect(() => {
    // 1. Define an async function to call the service
    const loadCurriculum = async () => {
      if (!user?.class) return;

      // 2. Dispatch the thunk and wait for the data it returns
      const curriculumData = await dispatch(fetchCurriculumByClass());

      // 3. Update state with the returned data
      if (curriculumData) {
        setCurriculumData(curriculumData);
        if (curriculumData.subjects) {
          const subjectList = Object.keys(curriculumData.subjects).map(
            (key) => ({
              id: key,
              name:
                key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " "),
            })
          );
          setSubjects(subjectList);
        }
      }
    };

    loadCurriculum();
  }, [user?.class, dispatch]);

  // Update chapters when subject changes
  useEffect(() => {
    if (
      selectedSubject &&
      curriculumData?.subjects?.[selectedSubject]?.chapters
    ) {
      setChapters(curriculumData.subjects[selectedSubject].chapters);
    } else {
      setChapters([]);
    }
    //console.log("cur", curriculumData);
  }, [selectedSubject, curriculumData]);
  //--------------------------------------------------------------------------------------------------------------

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (
        isSidebarOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(e.target)
      ) {
        setIsSidebarOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isSidebarOpen]);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }

    const fetchChats = async () => {
      const fetchFn = getUserConversations();
      const conversations = await fetchFn();

      const formatted = conversations.map((conv) => ({
        conversationId: conv._id,
        title: conv.title,
        messages: conv.messages.map((msg) => ({
          role: msg.sender,
          content: msg.content,
          imageUrl:msg.imageUrl || null,
          webLinks:msg.webLinks || null,
          youtubeLinks:msg.youtubeLinks || null,
        })),
      }));

      setChats(formatted);
      // setActiveChatIndex(0);

      //new part for /converstaionif
      if (routeConversationId) {
        const index = formatted.findIndex(
          (chat) => chat.conversationId === routeConversationId
        );
        if (index !== -1) {
          setActiveChatIndex(index);
        } else {
          setActiveChatIndex(0); // fallback
        }
      } else {
        setActiveChatIndex(0);
      }
    };
    //end

    fetchChats();
  }, [user, navigate, routeConversationId]);

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = { role: "user", content: input };
    const updatedChats = [...chats];
    const currentChat = updatedChats[activeChatIndex];

    currentChat.messages.push(userMessage);
    setChats(updatedChats);
    setInput("");
    setIsLoading(true);

    try {
      const conversationId = currentChat._id || currentChat.conversationId;
      if (!conversationId) throw new Error("Conversation ID not found");

      const addUserMessage = addMessage(conversationId, "user", input)();

      // const response = await chatbot({ query: input, conversationId : conversationId,student_class: user.class });

      let response;
      if (user.role === "teacher") {
        response = await teacherchatbot({
          query: input,
          conversationId: conversationId,
          className: curriculumData?._id,
          subjectName: selectedSubject,
          chapterName: selectedChapter,
        });
      } else {
        response = await chatbot({
          query: input,
          conversationId: conversationId,
          className: curriculumData?._id,
          subjectName: selectedSubject,
          chapterName: selectedChapter,
        });
      }
     // console.log("resss from chatbot st", response);


      let assistantContent;
      if (user.role === "teacher") {
        const botReply = response?.response || "";
        const cleanedResponse = botReply.replace(/^\$\$|\$\$$/g, "").trim();
        assistantContent = cleanedResponse || "Sorry, I could not understand.";
      } else {
        const botReply = response?.response || "";
        const cleanedResponse = botReply.replace(/^\$\$|\$\$$/g, "").trim();
        assistantContent = cleanedResponse || "Sorry, I could not understand.";
        // assistantContent =
        //   parseStreamedResponse(response) || "Sorry, I could not understand.";
      //  console.log("Assistant says:", assistantContent);
      }

     //console.log("resss from chatbot st", response);


    //  const assistantMessage = { role: "assistant", content: assistantContent };

      const imageUrl = response?.imageUrl || null
      const webLinks = response?.webLinks || null
      const youtubeLinks = response?.youtubeLinks || null

      const assistantMessage = {
        role: "assistant",
        content: assistantContent,
        imageUrl: imageUrl,
        webLinks: webLinks,
        youtubeLinks:youtubeLinks
      };

      const addBotMessage = addMessage(
        conversationId,
        "assistant",
        assistantContent,
        imageUrl,
        webLinks,
        youtubeLinks
      )();

      await Promise.all([addUserMessage, addBotMessage]);

      currentChat.messages.push(assistantMessage);
      setChats([...updatedChats]);
    } catch (error) {
      console.error("handleSend error:", error);
      currentChat.messages.push({
        role: "assistant",
        content: "Oops! Something went wrong. Please try again.",
      });
      setChats([...updatedChats]);
    } finally {
      setIsLoading(false);
    }
  };

  const generateChatName = (existingChats) => {
    const base = "Chat";
    let index = 1;
    while (existingChats.some((chat) => chat.title === `${base} ${index}`)) {
      index++;
    }
    return `${base} ${index}`;
  };

  const handleNewChat = async () => {
    try {
      const chatName = generateChatName(chats);
      const newConversation = await dispatch(createConversation(chatName, ""));

      if (!newConversation) return;

      const formatted = {
        conversationId: newConversation._id,
        title: newConversation.title || chatName,
        messages: newConversation.messages.map((msg) => ({
          role: msg.sender,
          content: msg.content,
        })),
      };

      setChats([...chats, formatted]);
      setActiveChatIndex(chats.length);
    } catch (error) {
      console.error("Failed to create new conversation:", error);
    }
  };

  const handleSelectChat = (index) => {
    setActiveChatIndex(index);
    setIsSidebarOpen(false);
  };

  // const handleEditTitle = async (conversationId, newTitle, index) => {
  //   try {
  //     const updateFn = updateConversationTitle(conversationId, newTitle);
  //     const updatedConversation = await updateFn();

  //     if (updatedConversation) {
  //       const updatedChats = [...chats];
  //       updatedChats[index].title = updatedConversation.title;
  //       setChats(updatedChats);
  //     }
  //   } catch (err) {
  //     console.error("Failed to update title:", err);
  //   } finally {
  //     setEditingIndex(null);
  //   }
  // };

  const handleEditTitle = async (conversationId, newTitle) => {
  try {
    const updateFn = updateConversationTitle(conversationId, newTitle);
    const updatedConversation = await updateFn();

    if (updatedConversation) {
      setChats((prevChats) =>
        prevChats.map((chat) =>
          chat.conversationId === conversationId
            ? { ...chat, title: updatedConversation.title }
            : chat
        )
      );
    }
  } catch (err) {
    console.error("Failed to update title:", err);
  } finally {
    setEditingIndex(null);
  }
  };


  // const handleDeleteChat = async (conversationId, index) => {
  //   try {
  //     const deleteFn = deleteConversation(conversationId);
  //     const success = await deleteFn();

  //     if (success) {
  //       const updatedChats = [...chats];
  //       updatedChats.splice(index, 1);
  //       setChats(updatedChats);
  //       setActiveChatIndex(Math.max(0, index - 1));
  //     }
  //   } catch (error) {
  //     console.error("Failed to delete conversation:", error);
  //   }
  // };


  const handleDeleteChat = async (conversationId) => {
  try {
    const deleteFn = deleteConversation(conversationId);
    const success = await deleteFn();

    if (success) {
      setChats((prevChats) => {
        const filtered = prevChats.filter(
          (chat) => chat.conversationId !== conversationId
        );
        const newActiveIndex = Math.max(
          0,
          prevChats.findIndex((chat) => chat.conversationId === conversationId) - 1
        );
        setActiveChatIndex(newActiveIndex);
        return filtered;
      });
    }
  } catch (error) {
    console.error("Failed to delete conversation:", error);
  }
  };


  return (
    <div className="flex min-h-[100dvh] sm:h-screen bg-gray-900 text-white relative">
      {/* Desktop Sidebar */}
      <div className="hidden md:block w-64 border-r border-gray-700">
        <ChatSidebar
          chats={chats}
          onNewChat={handleNewChat}
          onSelectChat={handleSelectChat}
          onDeleteChat={handleDeleteChat}
          onEditTitle={handleEditTitle}
          activeChatIndex={activeChatIndex}
          editingIndex={editingIndex}
          setEditingIndex={setEditingIndex}
        />
      </div>

      {/* Mobile Sidebar */}
      {isSidebarOpen && (
        <div className="absolute inset-0 z-50 bg-opacity-30 md:hidden">
          <div
            ref={sidebarRef}
            className="w-64 h-full bg-gray-800 border-r border-gray-700"
          >
            <ChatSidebar
              chats={chats}
              onNewChat={handleNewChat}
              onSelectChat={handleSelectChat}
              onDeleteChat={handleDeleteChat}
              onEditTitle={handleEditTitle}
              activeChatIndex={activeChatIndex}
              editingIndex={editingIndex}
              setEditingIndex={setEditingIndex}
            />
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col h-screen">
        {/* Mobile Nav */}
        <div className="md:hidden p-4 flex items-center justify-between border-b border-gray-700">
          <button onClick={() => setIsSidebarOpen(true)}>
            <Menu className="w-6 h-6 text-white" />
          </button>
          <Navbar />
        </div>

        {/* Desktop Nav */}
        <div className=" flex-shrink-0 hidden md:block">
          <Navbar />
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">
          <ChatWindow
            messages={chats[activeChatIndex]?.messages || []}
            isLoading={isLoading}
          />
        </div>
        {chats.length > 0 ? (
          <div className="flex-shrink-0">
            <ChatInput
              input={input}
              setInput={setInput}
              handleSend={handleSend}
              setChats={setChats}
              chats={chats}
              addMessage={addMessage}
              activeChatIndex={activeChatIndex}
              isLoading={isLoading}
              selectedSubject={selectedSubject}
              selectedChapter={selectedChapter}
              setSelectedSubject={setSelectedSubject}
              setSelectedChapter={setSelectedChapter}
              subjects={subjects}
              chapters={chapters}
            />
          </div>
        ) : (
          <div className="p-4 text-center text-gray-400 border-t border-gray-700">
            {t("chatbot.message")}
          </div>
        )}
      </div>
    </div>
  );
}
